// IMPORTANT: Keep this in sync with the section group definitions in permissionsCellTemplates.pug

import { FeatureFlags } from '../../ts/FeatureFlags';
import { PermissionRoleNames } from '../../ts/models/PermissionTemplateRoleDefinitions';
import { capabilities, capabilitiesByRole, capabilitiesReact, Capability, CapabilityInfo } from './Capabilities';
import { AuthType } from './PermissionsDialog';

export interface Section {
  locKey: string;
  capabilities: ReadonlyArray<CapabilityInfo>;
}

export interface SectionGroup {
  name: string;
  locKey: string;
  sections: ReadonlyArray<Section>;
}

export interface SectionReact {
  name: string;
  capabilities: ReadonlyArray<Capability>;
}

export interface SectionGroupReact {
  name: string;
  sections: ReadonlyArray<SectionReact>;
}

export const CapabilitySections: {[n: string]: () => ReadonlyArray<SectionReact>} = {
  [AuthType.PROJECT]: getProjectSectionsReact,
  [AuthType.PROJECT_DATASOURCE_TEMPLATE]: getDatasourceSectionsReact,
  [AuthType.PROJECT_DATA_ROLE_TEMPLATE]: getDataRoleSectionsReact,
  [AuthType.PROJECT_FLOW_TEMPLATE]: getFlowSectionsReact,
  [AuthType.PROJECT_METRIC_TEMPLATE]: getMetricsSectionsReact,
  [AuthType.PROJECT_WORKBOOK_TEMPLATE]: getWorkbookSectionsReact,
  [AuthType.DATABASE_TABLE_TEMPLATE]: getTableSectionsReact,
  [AuthType.WORKBOOK]: getWorkbookSectionsReact,
  [AuthType.VIEW]: getViewSectionsReact,
  [AuthType.DATASOURCE]: getDatasourceSectionsReact,
  [AuthType.DATAROLE]: getDataRoleSectionsReact,
  [AuthType.FLOW]: getFlowSectionsReact,
  [AuthType.METRIC]: getMetricsSectionsReact,
  [AuthType.DATABASE]: getDatabaseSectionsReact,
  [AuthType.TABLE]: getTableSectionsReact,
};

// Used for new react grid - duplicated to get rid of use of translate and locKey
// Need to be in sync with ts/models/PermissionTemplateRoleDefinitions.ts
function getDatabaseSectionsReact(): ReadonlyArray<SectionReact> {
  return [
    { name: PermissionRoleNames.View, capabilities: [capabilitiesReact.read] },
    { name: PermissionRoleNames.Publish, capabilities: [capabilitiesReact.write] },
    { name: PermissionRoleNames.Administer, capabilities: [capabilitiesReact.changePermissions] },
  ];
}

export function getDatasourceSectionsReact(): ReadonlyArray<SectionReact> {
  return [
    { name: PermissionRoleNames.View, capabilities: [capabilitiesReact.read, capabilitiesReact.connect] },
    { name: PermissionRoleNames.Explore, capabilities: [capabilitiesReact.exportXmlDatasource] },
    { name: PermissionRoleNames.Publish, capabilities: [capabilitiesReact.write] },
    { name: PermissionRoleNames.Administer, capabilities: [capabilitiesReact.delete, capabilitiesReact.changePermissions] },
  ];
}

export function getFlowSectionsReact(): ReadonlyArray<SectionReact> {
  return [
    { name: PermissionRoleNames.View, capabilities: [capabilitiesReact.read] },
    { name: PermissionRoleNames.Explore, capabilities: [capabilitiesReact.exportXmlFlow] },
    { name: PermissionRoleNames.Publish, capabilities: [capabilitiesReact.executeFlow, capabilitiesReact.write] },
    { name: PermissionRoleNames.Administer, capabilities: [capabilitiesReact.changeHierarchy, capabilitiesReact.delete, capabilitiesReact.changePermissions] },
  ];
}

export function getDataRoleSectionsReact(): ReadonlyArray<SectionReact> {
  return [
    { name: PermissionRoleNames.View, capabilities: [capabilitiesReact.read] },
    { name: PermissionRoleNames.Publish, capabilities: [capabilitiesReact.write] },
    { name: PermissionRoleNames.Administer, capabilities: [capabilitiesReact.changeHierarchy, capabilitiesReact.delete, capabilitiesReact.changePermissions] },
  ];
}

export function getTableSectionsReact(): ReadonlyArray<SectionReact> {
  return [
    { name: PermissionRoleNames.View, capabilities: [capabilitiesReact.read] },
    { name: PermissionRoleNames.Publish, capabilities: [capabilitiesReact.write] },
    { name: PermissionRoleNames.Administer, capabilities: [capabilitiesReact.changePermissions] }
  ];
}

export function getMetricsSectionsReact(): ReadonlyArray<SectionReact> {
  return [
    { name: PermissionRoleNames.View, capabilities: [capabilitiesReact.read] },
    { name: PermissionRoleNames.Publish, capabilities: [capabilitiesReact.write] },
    { name: PermissionRoleNames.Administer, capabilities: [capabilitiesReact.changeHierarchy, capabilitiesReact.delete, capabilitiesReact.changePermissions] },
  ];
}

export function getViewSectionsReact(): ReadonlyArray<SectionReact> {
  return [
    { name: PermissionRoleNames.View, capabilities: [capabilitiesReact.read, capabilitiesReact.filter, capabilitiesReact.viewComments, capabilitiesReact.addComment, capabilitiesReact.exportImage, capabilitiesReact.exportData] },
    { name: PermissionRoleNames.Explore, capabilities: [capabilitiesReact.shareView, capabilitiesReact.vud, capabilitiesReact.webAuthoring] },
    { name: PermissionRoleNames.Administer, capabilities: [capabilitiesReact.delete, capabilitiesReact.changePermissions] },
  ];
}

export function getWorkbookSectionsReact(): ReadonlyArray<SectionReact> {
  return [
    { name: PermissionRoleNames.View, capabilities: [capabilitiesReact.read, capabilitiesReact.filter, capabilitiesReact.viewComments, capabilitiesReact.addComment, capabilitiesReact.exportImage, capabilitiesReact.exportData] },
    { name: PermissionRoleNames.Explore, capabilities: [capabilitiesReact.shareView, capabilitiesReact.vud, capabilitiesReact.webAuthoring] },
    { name: PermissionRoleNames.Publish, capabilities: [capabilitiesReact.exportXmlWorkbook, capabilitiesReact.write] },
    { name: PermissionRoleNames.Administer, capabilities: [capabilitiesReact.changeHierarchy, capabilitiesReact.delete, capabilitiesReact.changePermissions] },
  ];
}

export function getProjectSectionsReact(): ReadonlyArray<SectionReact> {
  return [
    { name: PermissionRoleNames.View, capabilities: [capabilitiesReact.read] },
    { name: PermissionRoleNames.Publish, capabilities: [capabilitiesReact.writeProject] },
  ];
}
// Below is used for old angular grid

function getDatabaseSections() {
  return [
    { locKey: 'viewAndEdit', capabilities: [capabilities.read, capabilities.write, capabilities.changePermissions] },
  ];
}

export function getDatasourceSections(): ReadonlyArray<Section> {
  return [
    { locKey: 'use', capabilities: [capabilities.read, capabilities.connect] },
    { locKey: 'edit', capabilities: [capabilities.write, capabilities.exportXmlDatasource, capabilities.delete, capabilities.changePermissions] },
  ];
}

export function getFlowSections(): ReadonlyArray<Section> {
  return [
    { locKey: 'permissions_header_run',  capabilities: [capabilities.read, capabilities.executeFlow] },
    { locKey: 'edit', capabilities: [capabilities.write, capabilities.exportXmlFlow, capabilities.changeHierarchy, capabilities.delete, capabilities.changePermissions, ...(FeatureFlags.MaestroFlowEditingServerV1 ? [capabilities.webAuthoringForFlows] : [])] },
  ];
}

export function getDataRoleSections(): ReadonlyArray<Section> {
  return [
    { locKey: 'viewAndEdit', capabilities: [capabilities.read, capabilities.write, capabilities.changeHierarchy, capabilities.delete, capabilities.changePermissions] },
  ];
}

export function getTableSections() {
  return [
    { locKey: 'viewAndEdit', capabilities: [capabilities.read, capabilities.write, capabilities.changePermissions] },
  ];
}

export function getMetricsSections() {
  return [
    { locKey: 'viewAndEdit', capabilities: [capabilities.read, capabilities.write] },
  ];
}

export function getViewSections(): ReadonlyArray<Section> {
  return [
    { locKey: 'view',     capabilities: capabilitiesByRole.viewer },
    { locKey: 'interact', capabilities: capabilitiesByRole.interactor },
    { locKey: 'edit',     capabilities: [capabilities.delete, capabilities.changePermissions] },
  ];
}

export function getWorkbookSections(): ReadonlyArray<Section> {
  return [
    { locKey: 'permissions_header_view',     capabilities: capabilitiesByRole.viewer },
    { locKey: 'interact', capabilities: capabilitiesByRole.interactor },
    { locKey: 'edit',     capabilities: [capabilities.write, capabilities.exportXmlWorkbook, capabilities.changeHierarchy, capabilities.delete, capabilities.changePermissions] },
  ];
}

export function getProjectSections(): ReadonlyArray<Section> {
  return [
    { locKey: 'details', capabilities: [capabilities.read, capabilities.write, capabilities.projectLeader] },
  ];
}

export function getProjectSectionGroups(): ReadonlyArray<SectionGroup> {
  return [
    { name: 'project', locKey: 'project', sections: [{ locKey: 'details', capabilities: [capabilities.read, capabilities.write, capabilities.projectLeader]} ]},
    { name: 'projectWorkbookTemplate', locKey: 'workbooks', sections: getWorkbookSections() },
    { name: 'projectDatasourceTemplate', locKey: 'datasources', sections: getDatasourceSections() },
  ];
}

export function getProjectSectionGroupsWithFlowsAndDataRoles(): ReadonlyArray<SectionGroup> {
  return [
    { name: 'project', locKey: 'project', sections: [{ locKey: 'details', capabilities: [capabilities.read, capabilities.write, capabilities.projectLeader]} ]},
    { name: 'projectWorkbookTemplate', locKey: 'workbooks', sections: getWorkbookSections() },
    { name: 'projectDatasourceTemplate', locKey: 'datasources', sections: getDatasourceSections() },
    { name: 'projectFlowTemplate', locKey: 'flows', sections: getFlowSections() },
    { name: 'projectDataRoleTemplate', locKey: 'dataRoles', sections: getDataRoleSections() },
  ];
}

export function getDatasourceSectionGroups(): ReadonlyArray<SectionGroup> {
  return [
    { name: 'datasource', locKey: 'permissions', sections: getDatasourceSections() },
  ];
}

export function getFlowSectionGroups(): ReadonlyArray<SectionGroup> {
  return [
    { name: 'flow', locKey: 'permissions', sections: getFlowSections() },
  ];
}

export function getDataRoleSectionGroups(): ReadonlyArray<SectionGroup> {
  return [
    { name: 'dataRole', locKey: 'permissions', sections: getDataRoleSections() },
  ];
}

export function getViewSectionGroups(): ReadonlyArray<SectionGroup> {
  return [
    { name: 'view', locKey: 'permissions', sections: getViewSections() },
  ];
}

export function getWorkbookSectionGroups(): ReadonlyArray<SectionGroup> {
  return [
    { name: 'workbook', locKey: 'permissions', sections: getWorkbookSections() },
  ];
}

export function getDatabaseSectionGroups() {
    return [
        { name: 'database', locKey: 'database', sections: getDatabaseSections() },
        { name: 'databaseTableTemplate', locKey: 'tables', sections: getTableSections() },
    ];
}

export function getTableSectionGroups() {
    return [
        { name: 'table', locKey: 'permissions', sections: getTableSections() },
    ];
}
